import {
  differenceInDays,
  differenceInYears,
  eachMonthOfInterval,
  eachQuarterOfInterval,
  format,
  isBefore,
  parse,
} from 'date-fns';
import { dateToUTCMidnight, toUTCMidnight } from '@/lib/utils/common/dateFormats';

export interface NormalizedPlannedTimelineDates {
  hasAnyDate: boolean;
  start: string | undefined;
  finish: string | undefined;
}

export function getNormalizePlannedDates(
  plannedStartDateTime: string | null | undefined,
  plannedFinishDateTime: string | null | undefined,
): NormalizedPlannedTimelineDates {
  const start = plannedStartDateTime?.split('T')[0];
  const finish = plannedFinishDateTime?.split('T')[0];
  const hasAnyDate = !!(start || finish);
  const isInverted = start && finish && isBefore(finish, start);
  return {
    hasAnyDate,
    start: isInverted ? start : (start ?? finish),
    finish: isInverted ? start : (finish ?? start),
  };
}

export const getMonthName = (date: Date) => {
  return date.toLocaleString('en-US', { month: 'long', timeZone: 'UTC' }).toUpperCase();
};

export const formatDateHeader = (date: Date) => {
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${month}/${day}`;
};

export const getQuarter = (date: Date): number => {
  return Math.floor(date.getUTCMonth() / 3) + 1;
};

export const getQuarterLabel = (quarter: number, year: number): string => {
  return `Q${quarter} ${year}`;
};

const MAX_YEARS_FOR_MONTH_VIEW = 1;

export const shouldUseQuarterView = (from: string, to: string): boolean => {
  const fromDate = new Date(from);
  const toDate = new Date(to);
  const diffInYears = differenceInYears(toDate, fromDate);
  return diffInYears >= MAX_YEARS_FOR_MONTH_VIEW;
};

export const getTwoWeekRangesInMonth = ({
  year,
  month,
  from,
  to,
}: {
  year: number;
  month: number;
  from: string;
  to: string;
}) => {
  const fromDate = dateToUTCMidnight(parse(from, 'yyyy-MM-dd', new Date()));
  const toDate = dateToUTCMidnight(parse(to, 'yyyy-MM-dd', new Date()));

  const monthStart = toUTCMidnight(year, month, 1);
  const monthEnd = toUTCMidnight(year, month + 1, 0);

  const ranges: { start: Date; end: Date }[] = [];

  let currentStart = fromDate;

  while (currentStart <= toDate) {
    const currentEnd = toUTCMidnight(
      currentStart.getUTCFullYear(),
      currentStart.getUTCMonth(),
      currentStart.getUTCDate() + 13,
    );

    const rangeEnd = currentEnd > toDate ? toDate : currentEnd;

    if (currentStart >= monthStart && currentStart <= monthEnd) {
      ranges.push({
        start: currentStart,
        end: rangeEnd,
      });
    }

    currentStart = toUTCMidnight(
      currentStart.getUTCFullYear(),
      currentStart.getUTCMonth(),
      currentStart.getUTCDate() + 14,
    );
  }

  if (ranges.length === 0 && monthEnd < fromDate) {
    let backtrackStart = toUTCMidnight(
      fromDate.getUTCFullYear(),
      fromDate.getUTCMonth(),
      fromDate.getUTCDate() - 14,
    );

    while (backtrackStart >= monthStart) {
      const backtrackEnd = toUTCMidnight(
        backtrackStart.getUTCFullYear(),
        backtrackStart.getUTCMonth(),
        backtrackStart.getUTCDate() + 13,
      );

      const rangeEnd = toUTCMidnight(
        fromDate.getUTCFullYear(),
        fromDate.getUTCMonth(),
        fromDate.getUTCDate() - 1,
      );

      if (backtrackEnd >= monthStart && backtrackEnd <= monthEnd) {
        ranges.unshift({
          start: backtrackStart < monthStart ? monthStart : backtrackStart,
          end: backtrackEnd < rangeEnd ? backtrackEnd : rangeEnd,
        });
      } else if (backtrackEnd > monthEnd) {
        ranges.unshift({
          start: backtrackStart < monthStart ? monthStart : backtrackStart,
          end: rangeEnd < monthEnd ? rangeEnd : monthEnd,
        });
        break;
      }

      backtrackStart = toUTCMidnight(
        backtrackStart.getUTCFullYear(),
        backtrackStart.getUTCMonth(),
        backtrackStart.getUTCDate() - 14,
      );
    }
  }

  return ranges;
};

export type TimelineDateAxisMonth = {
  start: Date;
  end: Date;
  /** ISO date string (yyyy-MM-dd) of `start`. Used as the bucket key for items. */
  key: string;
  /** Calendar year of this slice's start date. */
  year: number;
  /** Calendar month index (0-11) of this slice's start date. */
  monthIndex: number;
  /** Sequential index within the parent quarter when useQuarterView. */
  indexInQuarter: number;
};

export type TimelineDateAxisQuarter = {
  year: number;
  quarter: number;
  quarterLabel: string;
  months: TimelineDateAxisMonth[];
};

export type TimelineDateAxisBiWeek = {
  start: Date;
  end: Date;
  /** ISO date string (yyyy-MM-dd) of `start`. Used as the bucket key for items. */
  key: string;
  /** Sequential index within the parent calendar month. */
  index: number;
};

export type TimelineDateAxisMonthGroup = {
  year: number;
  monthIndex: number;
  monthName: string;
  /** Calendar-month boundaries (clamped to scope). */
  visibleMonthStart: Date;
  visibleMonthEnd: Date;
  biWeeks: TimelineDateAxisBiWeek[];
  /** True when there are no bi-weeks intersecting the scope; column renderers
   *  fall back to a single empty cell spanning the visible month. */
  isEmptyState: boolean;
};

export type TimelineDateAxis =
  | {
      useQuarterView: true;
      startDateObj: Date;
      endDateObj: Date;
      isMoreThanHalfYear: boolean;
      quarters: TimelineDateAxisQuarter[];
    }
  | {
      useQuarterView: false;
      startDateObj: Date;
      endDateObj: Date;
      isMoreThanHalfYear: boolean;
      months: TimelineDateAxisMonthGroup[];
    };

/**
 * Builds the canonical date geometry used by both timeline column rendering and
 * item bucketing. Column callers walk the structure to produce columns with
 * width math; data callers flatten it via {@link flattenTimelineDateAxisToRanges}
 * to bucket items into the same keys. Keeping both paths anchored on this builder
 * guarantees that column accessors and item bucket keys stay aligned.
 */
export const buildTimelineDateAxis = ({
  iterationFrom,
  iterationTo,
  displayFrom,
  displayTo,
}: {
  iterationFrom: string;
  iterationTo: string;
  displayFrom?: string;
  displayTo?: string;
}): TimelineDateAxis | null => {
  const from = displayFrom ?? iterationFrom;
  const to = displayTo ?? iterationTo;
  const startDateObj = dateToUTCMidnight(parse(iterationFrom, 'yyyy-MM-dd', new Date()));
  const endDateObj = dateToUTCMidnight(parse(iterationTo, 'yyyy-MM-dd', new Date()));
  if (!startDateObj || !endDateObj) return null;

  const daysInScope = differenceInDays(endDateObj, startDateObj) + 1;
  const isMoreThanHalfYear = daysInScope > 182;
  const useQuarterView = shouldUseQuarterView(from, to);

  if (useQuarterView) {
    const quarters: TimelineDateAxisQuarter[] = [];
    for (const quarterDate of eachQuarterOfInterval({ start: startDateObj, end: endDateObj })) {
      const year = quarterDate.getFullYear();
      const quarter = getQuarter(quarterDate);
      const monthRanges = getTimelineColumnRangesInQuarter({ year, quarter, from, to });
      const monthsInScope = monthRanges
        .map((m) => ({
          start: m.start < startDateObj ? startDateObj : m.start,
          end: m.end > endDateObj ? endDateObj : m.end,
        }))
        .filter((m) => m.end >= startDateObj && m.start <= endDateObj);
      const months: TimelineDateAxisMonth[] = monthsInScope.map((m, indexInQuarter) => ({
        start: m.start,
        end: m.end,
        key: format(m.start, 'yyyy-MM-dd'),
        year: m.start.getUTCFullYear(),
        monthIndex: m.start.getUTCMonth(),
        indexInQuarter,
      }));
      quarters.push({
        year,
        quarter,
        quarterLabel: getQuarterLabel(quarter, year),
        months,
      });
    }
    return {
      useQuarterView: true,
      startDateObj,
      endDateObj,
      isMoreThanHalfYear,
      quarters,
    };
  }

  const months: TimelineDateAxisMonthGroup[] = [];
  for (const month of eachMonthOfInterval({ start: startDateObj, end: endDateObj })) {
    const year = month.getFullYear();
    const monthIndex = month.getMonth();
    const monthName = getMonthName(month);
    const calendarMonthStart = toUTCMidnight(year, monthIndex, 1);
    const calendarMonthEnd = toUTCMidnight(year, monthIndex + 1, 0);
    const visibleMonthStart = isBefore(startDateObj, calendarMonthStart)
      ? calendarMonthStart
      : startDateObj;
    const visibleMonthEnd = isBefore(calendarMonthEnd, endDateObj) ? calendarMonthEnd : endDateObj;

    const biWeekRanges = getTwoWeekRangesInMonth({ year, month: monthIndex, from, to });
    const biWeeksInScope = biWeekRanges
      .map((b) => ({
        start: b.start < startDateObj ? startDateObj : b.start,
        end: b.end > endDateObj ? endDateObj : b.end,
      }))
      .filter((b) => b.end >= startDateObj && b.start <= endDateObj);

    const biWeeks: TimelineDateAxisBiWeek[] = biWeeksInScope.map((b, index) => ({
      start: b.start,
      end: b.end,
      key: format(b.start, 'yyyy-MM-dd'),
      index,
    }));

    months.push({
      year,
      monthIndex,
      monthName,
      visibleMonthStart,
      visibleMonthEnd,
      biWeeks,
      isEmptyState: biWeeks.length === 0,
    });
  }

  return {
    useQuarterView: false,
    startDateObj,
    endDateObj,
    isMoreThanHalfYear,
    months,
  };
};

/**
 * Flattens a {@link TimelineDateAxis} to the list of `{ start, end, key }`
 * ranges used for bucketing items. Keys here match the column accessor keys
 * produced from the same axis, so an item bucketed into `key` ends up rendered
 * in the column that uses `accessor("items." + key)`.
 */
export const flattenTimelineDateAxisToRanges = (
  axis: TimelineDateAxis,
): { start: Date; end: Date; key: string }[] => {
  if (axis.useQuarterView) {
    const out: { start: Date; end: Date; key: string }[] = [];
    for (const quarter of axis.quarters) {
      for (const month of quarter.months) {
        out.push({ start: month.start, end: month.end, key: month.key });
      }
    }
    return out;
  }
  const out: { start: Date; end: Date; key: string }[] = [];
  for (const month of axis.months) {
    for (const biWeek of month.biWeeks) {
      out.push({ start: biWeek.start, end: biWeek.end, key: biWeek.key });
    }
  }
  return out;
};

export const getTimelineColumnRangesInQuarter = ({
  year,
  quarter,
  from,
  to,
}: {
  year: number;
  quarter: number;
  from: string;
  to: string;
}) => {
  const startMonth = (quarter - 1) * 3;
  const endMonth = startMonth + 2;

  const fromDate = dateToUTCMidnight(parse(from, 'yyyy-MM-dd', new Date()));
  const toDate = dateToUTCMidnight(parse(to, 'yyyy-MM-dd', new Date()));

  const months: { start: Date; end: Date }[] = [];

  for (let monthIndex = startMonth; monthIndex <= endMonth; monthIndex++) {
    const monthStart = new Date(year, monthIndex, 1);
    const monthEnd = new Date(year, monthIndex + 1, 0);
    const utcStart = dateToUTCMidnight(monthStart);
    const utcEnd = dateToUTCMidnight(monthEnd);
    if (utcEnd < fromDate || utcStart > toDate) {
      continue;
    }

    let rangeStart = utcStart;
    let rangeEnd = utcEnd;

    if (rangeStart < fromDate) {
      rangeStart = fromDate;
    }

    if (rangeEnd > toDate) {
      rangeEnd = toDate;
    }

    months.push({
      start: rangeStart,
      end: rangeEnd,
    });
  }

  return months;
};
